"use client";

import { useEffect, useState } from "react";
import { Menu, Phone } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { WvcLogo } from "@/integrations/wordpress/WvcLogo";
const tickerItems = ["LICENSED & INSURED", "FAMILY LEGACY PLANNING", "PHONE (800) 555-0199", "CONSULTATIONS MON-FRI 9A-5P", "NATIONWIDE COVERAGE", "SECURE YOUR FUTURE"];
function DesktopMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex gap-6">
        <div className="h-4 w-20 bg-muted/40 animate-pulse" />
        <div className="h-4 w-24 bg-muted/40 animate-pulse" />
        <div className="h-4 w-16 bg-muted/40 animate-pulse" />
        <div className="h-4 w-24 bg-muted/40 animate-pulse" />
      </div>;
  }
  return <ul className="flex items-center gap-8">
      {menuItems.map((item, i) => <li key={item.id} data-index={i} className="relative group">
          <Link to={item.href || "#"} className="text-foreground font-default text-xs uppercase tracking-wider font-semibold hover:text-primary transition-colors duration-200">
            {item.label}
            <span className="absolute -bottom-1.5 left-0 h-[2px] w-0 bg-primary transition-all duration-200 group-hover:w-full" />
          </Link>
        </li>)}
    </ul>;
}
function MobileMenuConsumer({
  onClose
}: {
  onClose: () => void;
}) {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex flex-col gap-4 px-6">
        <div className="h-8 w-3/4 bg-muted/40 animate-pulse" />
        <div className="h-8 w-2/3 bg-muted/40 animate-pulse" />
        <div className="h-8 w-3/4 bg-muted/40 animate-pulse" />
      </div>;
  }
  return <ul className="flex flex-col">
      {menuItems.map((item, i) => <li key={item.id} data-index={i} className="border-b border-border">
          <Link to={item.href || "#"} onClick={onClose} className="block px-6 py-5 text-foreground font-default font-bold uppercase tracking-wider text-lg hover:bg-accent hover:text-accent-foreground transition-colors">
            {item.label}
          </Link>
          <div className="h-2 w-full bg-primary" />
        </li>)}
    </ul>;
}
export default function Siteheader() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll, {
      passive: true
    });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        if (href === "/#") {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: "smooth"
          });
          return;
        }
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (hashPart === "") {
            e.preventDefault();
            window.scrollTo({
              top: 0,
              behavior: "smooth"
            });
            return;
          }
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <section data-section-id="1990" id="siteheader" className="sticky top-0 z-50 w-full bg-background text-foreground">
      <div className="hidden lg:block h-6 overflow-hidden border-b border-border relative bg-primary">
        <div className="flex animate-[ticker_40s_linear_infinite] whitespace-nowrap items-center h-full">
          {[...tickerItems, ...tickerItems, ...tickerItems].map((item, i) => <div data-index={i} key={i} className="flex items-center font-default text-[11px] tracking-widest font-medium px-6 text-primary-foreground">
              <span className="inline-block w-1.5 h-1.5 mr-6 bg-secondary" />
              {item}
            </div>)}
        </div>
        <style>{`
          @keyframes ticker {
            0% { transform: translateX(0); }
            100% { transform: translateX(-33.333%); }
          }
        `}</style>
      </div>

      <div className={`w-full border-b border-border transition-all duration-[260ms] ease-out ${scrolled ? "backdrop-blur-lg bg-background/90 shadow-md" : "bg-background"}`}>
        <div className="mx-auto max-w-[1360px] px-6 md:px-10 lg:px-16">
          <div className="grid grid-cols-12 items-center h-20 gap-4">
            <div className="col-span-6 lg:col-span-3 flex items-center">
              <Link to="/" className="flex items-center" aria-label="Family Legacy Planning home">
                <WvcLogo className="h-8 w-auto" />
              </Link>
            </div>

            <nav className="hidden lg:flex col-span-6 justify-center">
              <MenuProvider menu_id="16">
                <DesktopMenuConsumer />
              </MenuProvider>
            </nav>

            <div className="col-span-6 lg:col-span-3 flex items-center justify-end gap-4">
              <div className="hidden xl:flex items-center font-default text-[11px] uppercase tracking-widest text-muted-foreground">
                <Phone className="w-3 h-3 mr-2" strokeWidth={1.75} />
                9A-5P MON-FRI
              </div>

              <Button asChild className="hidden md:inline-flex h-11 px-6 rounded-lg font-default font-bold uppercase text-xs tracking-widest bg-secondary text-secondary-foreground hover:bg-secondary/90 transition-all duration-200 shadow-md">
                <Link to="/HomePage">Get My Quote</Link>
              </Button>

              <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
                <SheetTrigger asChild>
                  <Button variant="ghost" size="icon" className="lg:hidden h-11 w-11 rounded-lg text-foreground hover:bg-accent hover:text-accent-foreground" aria-label="Open menu">
                    <Menu className="w-6 h-6" strokeWidth={1.75} />
                  </Button>
                </SheetTrigger>
                <SheetContent side="right" className="w-full sm:max-w-md p-0 border-l border-border bg-background">
                  <SheetHeader className="px-6 py-6 border-b border-border">
                    <SheetTitle className="font-default text-xs uppercase tracking-widest text-foreground text-left">
                      <span className="inline-block w-2 h-2 bg-primary mr-2 align-middle" />
                      MENU
                    </SheetTitle>
                  </SheetHeader>

                  <div className="flex-1 overflow-y-auto">
                    <MenuProvider menu_id="16">
                      <MobileMenuConsumer onClose={() => setMobileOpen(false)} />
                    </MenuProvider>
                  </div>

                  <div className="border-t border-border p-6 space-y-4">
                    <div className="font-default text-[11px] uppercase tracking-widest text-muted-foreground">
                      <div className="flex items-center mb-2">
                        <Phone className="w-3 h-3 mr-2" strokeWidth={1.75} />
                        (800) 555-0199
                      </div>
                      <div>CONSULTATIONS MON-FRI 9A-5P</div>
                    </div>

                    <Button asChild className="w-full h-12 rounded-lg font-default font-bold uppercase text-xs tracking-widest bg-secondary text-secondary-foreground hover:bg-secondary/90 shadow-md">
                      <Link to="/HomePage" onClick={() => setMobileOpen(false)}>
                        Get My Quote
                      </Link>
                    </Button>
                  </div>
                </SheetContent>
              </Sheet>
            </div>
          </div>
        </div>
      </div>
    </section>;
}